import React from 'react';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import type { AlertColor } from '@mui/material';

import type { CalcConditionResult } from 'utils/rCalcConditionResult';

const ConditionListAlert: React.FC<{
    severity: AlertColor;
    title: string;
    caption?: string;
    labels: string[];
}> = ({ severity, title, caption, labels }) => {
    if (labels.length === 0) return null;
    return (
        <Alert severity={severity} sx={{ mt: 2 }}>
            <AlertTitle>{title}</AlertTitle>
            {caption && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>{caption}</Typography>
            )}
            <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                {labels.map((label) => (
                    <Typography component="li" variant="body2" key={label}>{label}</Typography>
                ))}
            </Box>
        </Alert>
    );
};

// R 側一括計算の 4 分類サマリ表示（3 計算コンポーネント共通）。
// succeeded / missing_data / failed の条件一覧を表示し、skipped_no_data は表示しない（issue #28）。
// 全カテゴリが空（全条件 skipped_no_data＝起点データ未登録）でも無反応にはせず、
// 条件一覧なしの案内だけを出す（一覧非表示の要件とフィードバック確保の両立）。
export const CalcConditionResultAlerts: React.FC<{ result: CalcConditionResult | null }> = ({ result }) => {
    if (!result) return null;
    const isEmpty =
        result.succeeded.length === 0 && result.missing_data.length === 0 && result.failed.length === 0;
    if (isEmpty) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                <AlertTitle>計算対象となる条件がありませんでした</AlertTitle>
                <Typography variant="body2">
                    データ登録ページから対象条件のデータを登録してください。
                </Typography>
            </Alert>
        );
    }
    return (
        <>
            <ConditionListAlert
                severity="success"
                title="計算が完了した条件"
                labels={result.succeeded}
            />
            <ConditionListAlert
                severity="warning"
                title="必要なデータが一部不足しているため計算できなかった条件"
                caption="一部のデータは登録済みですが、計算に必要なデータが揃っていません。データ登録ページから不足しているデータを登録してください。"
                labels={result.missing_data}
            />
            <ConditionListAlert
                severity="error"
                title="計算に失敗した条件"
                labels={result.failed}
            />
        </>
    );
};
